import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import Input from "../../Input";
import { UIPhoneField, UITextField } from "../../ui";
import { useSelector } from "react-redux";
import messages from "../../../assets/locale/messages";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { clx } from "../../../utils/Helpers";

import "./_CompanyEditForm.scss";

const ContactPersonSection = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  ownerEmail,
  disabled,
  hasTitleIcon,
  classNames,
  hideTitle,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { companiesList: cl, inputsValidations: iv } = messages[lang];
  const ownerNameHasError = !!(touched.owner_name && errors.owner_name);
  const phoneHasError = !!(
    touched.owner_phone_number && errors.owner_phone_number
  );
  const validationText = (key, fallbackKey = "generalFieldRequired") =>
    iv[key] || iv[fallbackKey] || key;

  return (
    <Box className={clx("section", classNames.base)}>
      {!hideTitle && (
        <div className="d-flex align-items-center gap-2">
          {hasTitleIcon && (
            <PersonOutlineIcon sx={{ fontSize: 16, color: "#16A34A" }} />
          )}
          <Typography className={clx("section-title", classNames.title)}>
            {cl.contactPerson}
          </Typography>
        </div>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          className={clx(
            "section__input-group-name",
            classNames.inputGroupName,
          )}
        >
          <Input
            name="owner_name"
            label={cl.fullName}
            value={values.owner_name}
            required
            disabled={disabled}
            onChange={(val) => {
              setFieldTouched("owner_name");
              setFieldValue("owner_name", val);
            }}
            isInputHasErr={ownerNameHasError}
            errMsg={
              ownerNameHasError && iv[errors.owner_name]
                ? errors.owner_name
                : "nameRequired"
            }
          />

          <UIPhoneField
            name="owner_phone_number"
            label={cl.mobileNumber}
            required
            country={values.owner_country_short}
            countryCode={String(values.owner_dial_code || "+966").replace(
              /^\+/,
              "",
            )}
            preferredCountries={["eg", "sa", "ae"]}
            value={values.owner_phone_number || ""}
            onChange={(phoneValue, countryObj) => {
              // Formik validates each setter against the pre-event values, so only the
              // phone setter (called last) validates — otherwise the error lags a keystroke.
              setFieldTouched("owner_phone_number", true, false);
              if (countryObj?.dialCode != null) {
                const dc = String(countryObj.dialCode).replace(/\D/g, "");
                setFieldValue("owner_dial_code", `+${dc}`, false);
              }
              if (countryObj?.countryCode) {
                setFieldValue(
                  "owner_country_short",
                  String(countryObj.countryCode).toLowerCase(),
                  false,
                );
              }
              setFieldValue("owner_phone_number", phoneValue);
            }}
            onBlur={() => setFieldTouched("owner_phone_number", true)}
            error={phoneHasError}
            helperText={
              phoneHasError
                ? validationText(errors.owner_phone_number)
                : ""
            }
            disabled={disabled}
          />
        </Box>

        <UITextField
          name="owner_email"
          label={cl.emailAddress}
          value={ownerEmail}
          disabled
          helperText={
            <span className="helper-text">
              <ErrorOutlineOutlinedIcon sx={{ fontSize: 12 }} />{" "}
              {cl.cannotBeEditedEmail}
            </span>
          }
        />
      </Box>
    </Box>
  );
};

ContactPersonSection.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  ownerEmail: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  hasTitleIcon: PropTypes.bool,
  classNames: PropTypes.object,
  hideTitle: PropTypes.bool,
};

ContactPersonSection.defaultProps = {
  disabled: false,
  hasTitleIcon: false,
  classNames: {
    base: "",
    title: "",
    inputGroupName: "",
  },
  hideTitle: false,
};

export default ContactPersonSection;
